import { CommonModule } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    merge,
    of,
    share,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap,
    timer
} from 'rxjs';
import { DisableCountParam, DisableSortParam, LoadingUIEnabled } from '../../loading.interceptor';
import { EmphasisDirective } from '../../shared/directives/emphasis.directive';

type StockOpt = { reference: string; display: string; inStock: boolean, ingredients: string };
type RxOpt = { code: string; display: string; system: string; ingredients: string[] };

export type MedicineSearchResult =
    | {
        kind: 'stock';
        reference: string;
        /** Submission-friendly display value (used later when building FHIR Reference.display) */
        display: string;
        inStock: boolean;
        ingredients: string;
        /** UI-only label shape for uniform rendering */
        label: { display: string; inStock?: boolean; ingredients?: string };
    }
    | {
        kind: 'rxnorm';
        code: string;
        /** Submission-friendly display value */
        display: string;
        system: string;
        ingredients: string[];
        /** UI-only label shape for uniform rendering */
        label: { display: string; inStock?: boolean; ingredients?: string };
    }
    | {
        kind: 'userExactWords';
        /** Submission-friendly display value */
        display: string;
        system: 'user/input';
        /** UI-only label shape for uniform rendering */
        label: { display: string; inStock?: boolean; ingredients?: string };
    };

@Component({
    selector: 'app-medicine-search-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatInputModule,
        MatProgressSpinnerModule,
        MatIconModule,
        EmphasisDirective
    ],
    templateUrl: './medicine-search-dialog.component.html'
})
export class MedicineSearchDialogComponent {
    private http = inject(HttpClient);
    dialogRef = inject(MatDialogRef<MedicineSearchDialogComponent>);

    // FIX: use inject(...) so it's available during field initializers
    private data = inject<{ stock: StockOpt[] }>(MAT_DIALOG_DATA);
    private stock$ = of(this.data?.stock ?? []);

    rxNormMedicationCtrl = new FormControl<string>('');
    rxNormSearching$ = new BehaviorSubject<boolean>(false);

    private rxNormTerm$ = this.rxNormMedicationCtrl.valueChanges.pipe(
        startWith(''),
        map(v => (v ?? '').toString().trim()),
        distinctUntilChanged(),
        share()
    );

    private rxNormCachedOptions$ = new BehaviorSubject<RxOpt[]>([]);

    private mergeRxNormCache(next: RxOpt[]) {
        if (!next?.length) return;
        const prev = this.rxNormCachedOptions$.getValue() || [];
        const seen = new Set(prev.map(p => `${p.system}:${p.code}`));
        const merged = [...prev];
        for (const n of next) {
            const key = `${n.system}:${n.code}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(n);
        }
        this.rxNormCachedOptions$.next(merged);
    }

    rxNormMergedMedicationOptions$ = combineLatest([
        this.rxNormTerm$,
        this.stock$
    ]).pipe(
        debounceTime(250),
        switchMap(([term, stock]) => {
            const cleaned = (term ?? '').trim();

            if (!cleaned) {
                return of({ term: '', stock, rx: this.rxNormCachedOptions$.getValue() || [] });
            }

            // fresh search of this term (current behavior of your AddMedicineRequestsComponent)
            this.rxNormCachedOptions$.next([]);
            this.rxNormSearching$.next(true);

            if (cleaned.length < 2) {
                this.rxNormSearching$.next(false);
                return of({ term: cleaned, stock, rx: this.rxNormCachedOptions$.getValue() || [] });
            }

            return this.searchRxNormSBD(cleaned).pipe(
                tap((fresh) => this.mergeRxNormCache(fresh)),
                catchError(() => of([] as Array<RxOpt>)),
                map(() => ({ term: cleaned, stock, rx: this.rxNormCachedOptions$.getValue() || [] }))
            );
        }),
        tap(() => this.rxNormSearching$.next(false)),
        map(({ term, stock, rx }) => {
            const t = (term ?? '').toLowerCase();

            const filteredStock = !t
                ? (stock || [])
                : (stock || []).filter((s) => `${s.display ?? ''} ${s.reference ?? ''}`.toLowerCase().includes(t));


            //     kind: 'stock' as const,
            //     reference: s.reference,
            //     label: {
            //       display: s.display,
            //       inStock: s.inStock,
            //       ingredients: s.ingredients
            //     },
            //     display: s.display.replace(/\s*\(stock:\s*[^)]*\)\s*$/i, '') + s.ingredients,
            //     inStock: s.inStock,
            //     ingredients: s.ingredients
            //   }));

            const stockItems: MedicineSearchResult[] = filteredStock.map((s) => {
                const baseDisplay = (s.display ?? '').toString();
                const ing = (s.ingredients ?? '').toString();
                return {
                    kind: 'stock',
                    reference: s.reference,
                    display: baseDisplay.replace(/\s*\(stock:\s*[^)]*\)\s*$/i, '') + s.ingredients,
                    inStock: s.inStock,
                    ingredients: ing,
                    label: {
                        display: baseDisplay.replace(/\s*\(stock:\s*[^)]*\)\s*$/i, ''),
                        inStock: s.inStock,
                        ingredients: ing
                    }
                };
            });
            console.log('Filtered stock items:', stockItems);


            const rxItems: MedicineSearchResult[] = (rx || []).map((c) => {
                const ingredientsLabel = (c.ingredients?.length) ? `${c.display ? '(Ingredients:' : ''} ${c.ingredients.join(', ')} ${c.display ? ')' : ''}` : '';
                return {
                    kind: 'rxnorm',
                    code: c.code,
                    display: `${c.display} ${ingredientsLabel}`,
                    system: c.system,
                    ingredients: c.ingredients,
                    label: {
                        display: c.display,
                        ...(ingredientsLabel ? { ingredients: ingredientsLabel } : {})
                    }
                };
            });

            const extras: MedicineSearchResult[] =
                term.trim().length > 1 && !term.includes('object Object')
                    ? [{
                        kind: 'userExactWords',
                        display: term,
                        system: 'user/input',
                        label: { display: term }
                    }]
                    : [];

            const seen = new Set<string>();
            return [...stockItems, ...rxItems, ...extras].filter((x: any) => {
                const key = x.kind === 'stock' ? x.reference : `${x.kind}:${x.system}:${x.code ?? x.display}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            });
        }),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    // same slow-loader idea, but local to dialog
    loading$ = this.rxNormTerm$.pipe(
        switchMap(() =>
            this.rxNormSearching$.pipe(
                switchMap(inFlight => {
                    if (!inFlight) return of(false);
                    return timer(250).pipe(
                        mapTo(true),
                        takeUntil(
                            merge(
                                this.rxNormTerm$,
                                this.rxNormSearching$.pipe(filter(active => !active))
                            )
                        )
                    );
                })
            )
        ),
        startWith(false)
    );

    displayRxNormOption = (opt: any) => this.stripStockSuffix(opt?.label ?? '');

    private stripStockSuffix(label: string): string {
        return (label ?? '')
            .toString()
            .replace(/\s*\(stock:\s*[^)]*\)\s*$/i, '')
            .replace(/^use\s\"(.*)\"$/i, '$1')
            .trim();
    }

    selectOption(opt: MedicineSearchResult) {
        this.dialogRef.close(opt);
    }

    clearSearch() {
        this.rxNormMedicationCtrl.setValue('');
    }

    private isRxNormPackOrKit(display: string): boolean {
        const s = (display ?? '').toString();
        return s.includes('{') || /\bPack\b/i.test(s);
    }

    private simplifyRxNormDisplay(raw: string): string {
        const s = (raw ?? '').toString().trim();
        if (!s) return '';
        const brandMatch = s.match(/\[([^\]]+)\]/);
        if (brandMatch?.[1]?.trim()) return brandMatch[1].trim();
        return '';
    }

    private extractRxNormIngredients(rawDisplay: string): string[] {
        const ingredients = [...(rawDisplay ?? '').toString().split('/')];
        if (ingredients.length) ingredients[ingredients.length - 1] = ingredients[ingredients.length - 1].split('[')[0];
        return ingredients;
    }

    private searchRxNormSBD(term: string) {
        const url = `https://rxnav.nlm.nih.gov/REST/drugs.json?type=SBD&name=${encodeURIComponent(term)}`;

        const context = new HttpContext()
            .set(LoadingUIEnabled, false)
            .set(DisableCountParam, true)
            .set(DisableSortParam, true);

        return this.http.get<any>(url, { context }).pipe(
            map((res: any): RxOpt[] => {
                const system = 'http://www.nlm.nih.gov/research/umls/rxnorm';
                const groups = res?.drugGroup?.conceptGroup;
                const list = Array.isArray(groups) ? groups : (groups ? [groups] : []);

                const out: RxOpt[] = [];

                for (const g of list) {
                    const tty = (g?.tty ?? '').toString().toUpperCase();
                    if (tty && tty !== 'SBD' && tty !== 'SCD') continue;

                    const props = Array.isArray(g?.conceptProperties) ? g.conceptProperties : [];
                    for (const cp of props) {
                        const code = (cp?.rxcui ?? '').toString().trim();
                        const rawDisplay = (cp?.name ?? '').toString().trim();
                        if (!code || !rawDisplay) continue;

                        if (this.isRxNormPackOrKit(rawDisplay)) continue;

                        const display = this.simplifyRxNormDisplay(rawDisplay);
                        out.push({
                            code,
                            display,
                            system,
                            ingredients: this.extractRxNormIngredients(rawDisplay)
                        });
                    }
                }

                const seen = new Set<string>();
                return out.filter(x => (seen.has(x.code) ? false : (seen.add(x.code), true)));
            })
        );
    }
}
